<template>
  <div class="analytic-kpi-card quick-action-card" @click="$emit('click')" :title="clickTitle">
    <div class="kpi-card-header d-flex justify-content-between align-items-center mb-3">
      <span class="kpi-label" :class="labelClass">{{ label }}</span>
      <div class="icon-circle primary-icon-circle kpi-icon-wrapper">
        <i :class="['fas', defaultIcon, 'default-icon']"></i>
        <i class="fas fa-external-link-alt hover-icon"></i>
      </div>
    </div>
    <div class="kpi-body">
      <h2 class="kpi-value mb-2" :class="{'long-value': String(value).length > 11}">{{ value }}</h2>
      <div class="kpi-footer d-flex align-items-center" :class="footerJustify">
        <div class="d-flex align-items-center gap-1">
          <span class="trend-indicator" :class="trend > 0 ? 'trend-up' : 'trend-down'">
            <i class="fas" :class="trend > 0 ? 'fa-arrow-up' : 'fa-arrow-down'"></i>
            {{ Math.abs(trend) }}%
          </span>
          <span
            class="kpi-compare-text"
            :title="timeframeText.charAt(0).toUpperCase() + timeframeText.slice(1) + ': ' + previousValue"
          >vs {{ timeframeText }}</span>
        </div>
        <!-- Optional trailing stat (e.g. "Total: 1234") -->
        <span v-if="totalLabel" class="kpi-total-text text-muted" style="font-size: 0.8rem;" :title="totalTitle">
          {{ totalLabel }}: {{ totalValue }}
        </span>
      </div>
    </div>
    <!-- Optional bottom slot (e.g. cycling dots) -->
    <slot />
  </div>
</template>

<script>
/**
 * KpiCard — statistic card used in the top KPI row of the dashboard.
 *
 * Props:
 *   label         (String)  — uppercase metric name (e.g. 'NEW MEMBERS').
 *   labelClass    (String)  — optional extra class for coloured labels (e.g. 'text-indigo-dark').
 *   defaultIcon   (String)  — Font Awesome icon class (e.g. 'fa-user-plus').
 *   value         (*)       — primary displayed value.
 *   trend         (Number)  — percentage change vs. comparison period (positive = up).
 *   previousValue (*)       — comparison period value shown in tooltip.
 *   timeframeText (String)  — human-readable period label (e.g. 'last month').
 *   totalLabel    (String)  — optional label for secondary trailing stat (e.g. 'Total').
 *   totalValue    (*)       — value for secondary trailing stat.
 *   totalTitle    (String)  — tooltip for secondary trailing stat.
 *   clickTitle    (String)  — title attribute for click navigation hint.
 *
 * Emits:
 *   click — fired when the card is clicked.
 */
export default {
    name: 'KpiCard',
    emits: ['click'],
    props: {
        label:         { type: String,  required: true },
        labelClass:    { type: String,  default: null },
        defaultIcon:   { type: String,  required: true },
        value:         { default: '-' },
        trend:         { type: Number,  default: 0 },
        previousValue: { default: 0 },
        timeframeText: { type: String,  required: true },
        totalLabel:    { type: String,  default: null },
        totalValue:    { default: null },
        totalTitle:    { type: String,  default: '' },
        clickTitle:    { type: String,  default: '' },
    },
    computed: {
        footerJustify() {
            return this.totalLabel ? 'justify-content-between' : 'gap-1';
        }
    }
}
</script>

<style scoped>
/* ──────────────────────────────────────────────────────────────
   All styles are exact copies from Desktop.vue. Nothing new added.
   ────────────────────────────────────────────────────────────── */

.analytic-kpi-card {
    background: #ffffff;
    border: 1px solid #f1f5f9;
    border-radius: 12px;
    padding: 24px;
    position: relative;
    min-height: 160px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    transition: transform 0.22s ease-in-out, border-color 0.22s ease-in-out, box-shadow 0.22s ease-in-out;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}
.analytic-kpi-card:hover {
    transform: translateY(-2px);
    border-color: var(--primary-color, #F27178);
    box-shadow: 0 6px 16px rgba(242, 113, 120, 0.08);
}

.quick-action-card { cursor: pointer; }

.kpi-icon-wrapper .hover-icon { display: none; }
.quick-action-card:hover .kpi-icon-wrapper .default-icon { display: none; }
.quick-action-card:hover .kpi-icon-wrapper .hover-icon { display: inline-block; }

.kpi-label {
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    color: #64748b;
}
.kpi-value {
    color: #0f172a;
    font-size: 2rem;
    letter-spacing: -0.03em;
    line-height: 1;
}
.kpi-value.long-value {
    font-size: 1.5rem;
    word-break: break-word;
}
.trend-indicator {
    font-size: 0.725rem;
    font-weight: 700;
    padding: 2px 6px;
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
}
.trend-up  { background-color: #dcfce7; color: #15803d; }
.trend-down { background-color: #fee2e2; color: #b91c1c; }

.kpi-compare-text { font-size: 0.725rem; color: #64748b; font-weight: 500; }

.icon-circle {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
}
.primary-icon-circle {
    background-color: color-mix(in srgb, var(--primary-color, #F27178) 12%, transparent);
    color: var(--primary-color, #F27178);
}
</style>
